<template>
    <div class="change-group-fields">
        <div class="form-question">
            <h3 class="question-title">
                Do you need access to 
                <span class="tooltip-container">
                    Request for Proposals (RFPs)
                    <span class="tooltip">
                        Use an RFP to signal demand for a dataset not yet listed. Data providers can respond with proposals.                    </span>
                </span>? 
                <span class="required">*</span>
            </h3>
            
            <div class="radio-group">
                <label class="radio-option">
                    <input 
                        type="radio" 
                        v-model="formData.needsRFP" 
                        value="yes"
                        name="rfp-access"
                        data-testid="rfp-yes-radio"
                    />
                    <span class="radio-custom"></span>
                    Yes
                </label>
                <label class="radio-option">
                    <input 
                        type="radio" 
                        v-model="formData.needsRFP" 
                        value="no"
                        name="rfp-access"
                        data-testid="rfp-no-radio"
                    />
                    <span class="radio-custom"></span>
                    No
                </label>
            </div>

            <textarea v-if="formData.needsRFP === 'yes'"
                v-model="formData.rfpDescription"
                class="custom-textarea"
                placeholder="Please tell us about the dataset you are looking for, or a recent experience where you would have benefited from issuing an RFP."
                rows="4"
            ></textarea>
        </div>

        <div class="form-question">
            <h3 class="question-title">
                Do you want to be able to sell datasets? 
                <span class="required">*</span>
            </h3>
            
            <div class="radio-group">
                <label class="radio-option">
                    <input 
                        type="radio" 
                        v-model="formData.wantToSell" 
                        value="yes"
                        name="sell-datasets"
                        data-testid="seller-yes-radio"
                    />
                    <span class="radio-custom"></span>
                    Yes
                </label>
                <label class="radio-option">
                    <input 
                        type="radio" 
                        v-model="formData.wantToSell" 
                        value="no"
                        name="sell-datasets"
                        data-testid="seller-no-radio"
                    />
                    <span class="radio-custom"></span>
                    No
                </label>
            </div>
            <div v-if="formData.wantToSell === 'yes'">
                <textarea
                v-model="formData.sellDescription"
                class="custom-textarea"
                placeholder="Please tell us about the datasets you would like to sell on our platform."
                rows="4"
            ></textarea>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, defineEmits, watch } from 'vue'

const formData = ref({
    needsRFP: 'no',
    rfpDescription: '',
    wantToSell: 'no',
    sellDescription: ''
})

const emit = defineEmits(['change'])

const handleChange = () => {
    emit('change', formData.value)
}

watch(formData, () => {
    handleChange()
}, { deep: true })

</script>

<style scoped lang="postcss">
.form-question {
    margin-bottom: 1.5rem;
}

.question-title {
    display: block;
    color: white;
    font-size: 0.875rem;
    font-weight: 500;
    margin-bottom: 0.5rem;
}

@media (min-width: 1536px) {
    .question-title {
        font-size: 1rem;
    }
}

.required {
    color: #ef4444;
}

.radio-group {
    display: flex;
    gap: 1.5rem;
    margin-bottom: 0.75rem;
}

.radio-option {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
    font-size: 0.875rem;
    color: #d1d5db;
}

@media (min-width: 1536px) {
    .radio-option {
        font-size: 1rem;
    }
}

.radio-option input[type="radio"] {
    display: none;
}

.radio-custom {
    @apply w-4 h-4 border border-echoes-primary rounded-full relative transition-all duration-300 flex-shrink-0;
 
}

.radio-option input[type="radio"]:checked + .radio-custom {
    @apply bg-echoes-primary border-echoes-primary;
}

.radio-option input[type="radio"]:checked + .radio-custom::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 6px;
    height: 6px;
    background-color: white;
    border-radius: 50%;
}

.radio-option:hover .radio-custom {
    @apply border-echoes-primary/80
}


    .custom-textarea {
        @apply w-full text-sm h-full px-4 py-3 bg-echoes-background-black text-white border border-echoes-border-gray rounded-[13px] placeholder-white/70 focus:outline-none  focus:border-echoes-primary resize-none transition-all duration-200;
    
        &::placeholder {
            @apply transition-all duration-200 text-white/50 hover:text-white/80;
        }
    
        &:hover {
            @apply bg-echoes-background-black
            border-white/20
            text-white;
        }
    
        &:focus::placeholder {
            @apply opacity-0 translate-x-1;
        }
    
        &:disabled {
            @apply cursor-not-allowed opacity-60 bg-echoes-background-gray/30;
        }
    }

@media (min-width: 1536px) {
    .custom-textarea {
        padding: 1rem;
        font-size: 1rem;
    }
}

.form-textarea::placeholder {
    color: #9ca3af;
}


@media (max-width: 768px) {
    .radio-group {
        flex-direction: column;
        gap: 0.75rem;
    }
    
    .question-title {
        font-size: 0.875rem;
    }
    
    .form-question {
        margin-bottom: 1.25rem;
    }
}

/* Tooltip styles */
.tooltip-container {
    @apply text-echoes-primary hover:text-echoes-primary/80;
    position: relative;
    cursor: help;
    text-decoration: none;
}

.tooltip {
    @apply invisible opacity-0 absolute transform -translate-x-1/2 
           bg-echoes-background-gray text-white text-xs rounded-md
           px-2 py-1 z-[99999] w-[280px] leading-relaxed font-normal
           border border-echoes-border-gray
           shadow-lg transition-all duration-200 pointer-events-none;
    top: 100%;
    left: 50%;
    margin-top: 5px;
}

.tooltip::after {
    @apply absolute -top-[5px] left-1/2 -ml-[5px] border-[5px] border-solid;
    content: '';
    border-color: transparent transparent rgb(31 41 55) transparent;
}

.tooltip-container:hover .tooltip {
    @apply visible opacity-100;
}

@media (max-width: 768px) {
    .tooltip {
        @apply w-[240px] text-[0.7rem] px-3 py-2;
        top: 100%;
        margin-top: 5px;
    }
}
</style>